import React, { useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MessageSquare,
  Upload,
  FileSpreadsheet,
  Phone,
  Send,
  Clock,
  Calendar,
  AlertTriangle,
  CheckCircle,
  X,
  Eye,
  Loader2,
  Smartphone,
  Info,
  Hash,
  Users,
  RefreshCw,
  PlayCircle,
  Pause
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// GSM-7 character set for proper encoding calculation
const GSM7_CHARS = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXTENDED = '^{}\\[~]|€';

const SMSMailing = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  
  // Campaign settings
  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState('marketing'); // 'marketing' | 'transactional'
  const [message, setMessage] = useState('');
  const [senderId, setSenderId] = useState('');
  
  // Recipients
  const [recipientSource, setRecipientSource] = useState('manual'); // 'manual' | 'file'
  const [manualNumbers, setManualNumbers] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileContacts, setFileContacts] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [phoneColumn, setPhoneColumn] = useState('');
  
  // Scheduling
  const [sendMode, setSendMode] = useState('immediate'); // 'immediate' | 'scheduled' | 'recurring'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [recurringPattern, setRecurringPattern] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
  const [recurringDays, setRecurringDays] = useState([]);
  
  // Test
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);
  
  // Preview
  const [showPreview, setShowPreview] = useState(false);
  
  // Loading states
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Calculate SMS metrics
  const calculateSMSMetrics = useCallback((text) => {
    if (!text) return { chars: 0, smsCount: 1, encoding: 'GSM-7', maxChars: 612 };
    
    // Add STOP message for marketing (stopsms.io URL after line break)
    let fullText = text;
    if (campaignType === 'marketing') {
      fullText += '\nstopsms.io';
    }
    
    // Check if all characters are GSM-7
    let isGSM7 = true;
    let extendedCount = 0;
    
    for (const char of fullText) {
      if (GSM7_EXTENDED.includes(char)) {
        extendedCount++;
      } else if (!GSM7_CHARS.includes(char)) {
        isGSM7 = false;
        break;
      }
    }
    
    const encoding = isGSM7 ? 'GSM-7' : 'Unicode';
    const charCount = isGSM7 ? fullText.length + extendedCount : fullText.length;
    
    let smsCount, maxChars;
    if (isGSM7) {
      // Max 612 chars = 4 SMS concatenated (153 chars per segment after header)
      maxChars = 612;
      if (charCount <= 160) {
        smsCount = 1;
      } else {
        // Concatenated SMS: 153 chars per segment (7 chars for header)
        smsCount = Math.ceil(charCount / 153);
      }
    } else {
      // Unicode: max 268 chars = 4 SMS (67 chars per segment)
      maxChars = 268;
      if (charCount <= 70) {
        smsCount = 1;
      } else {
        smsCount = Math.ceil(charCount / 67);
      }
    }
    
    return { chars: charCount, smsCount, encoding, maxChars, fullText };
  }, [campaignType]);

  const smsMetrics = calculateSMSMetrics(message);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
      toast.error('Format non supporté. Utilisez CSV ou Excel.');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/sms-mailing/parse-file`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadedFile({
        name: file.name,
        size: file.size,
        type: file.type
      });
      setFileContacts(response.data.contacts || []);
      setFileColumns(response.data.columns || []);
      
      // Auto-detect phone column
      const phoneColNames = ['mobile', 'tel', 'telephone', 'phone', 'numero', 'numéro', 'gsm'];
      const detectedCol = response.data.columns.find(col => 
        phoneColNames.includes(col.toLowerCase())
      );
      if (detectedCol) {
        setPhoneColumn(detectedCol);
      }
      
      toast.success(`${response.data.contacts?.length || 0} contacts chargés`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du chargement du fichier');
    } finally {
      setUploading(false);
    }
  };

  // Get recipients count
  const getRecipientsCount = () => {
    if (recipientSource === 'file') {
      return fileContacts.length;
    }
    return manualNumbers.split(/[\n,;]/).filter(n => n.trim()).length;
  };

  // Send test SMS
  const handleSendTest = async () => {
    if (!testPhone) {
      toast.error('Entrez un numéro de test');
      return;
    }
    if (!message) {
      toast.error('Entrez un message');
      return;
    }
    
    setTestSending(true);
    try {
      await axios.post(`${API_URL}/sms-mailing/send-test`, {
        phone: testPhone,
        message: smsMetrics.fullText,
        sender_id: senderId || 'B2RCS'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SMS de test envoyé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setTestSending(false);
    }
  };

  // Send campaign
  const handleSendCampaign = async () => {
    if (!campaignName) {
      toast.error('Donnez un nom à votre campagne');
      return;
    }
    if (!message) {
      toast.error('Entrez un message');
      return;
    }
    if (getRecipientsCount() === 0) {
      toast.error('Ajoutez des destinataires');
      return;
    }
    
    setSending(true);
    try {
      const recipients = recipientSource === 'file'
        ? fileContacts.map(c => c[phoneColumn])
        : manualNumbers.split(/[\n,;]/).filter(n => n.trim());
      
      const payload = {
        name: campaignName,
        type: campaignType,
        message: smsMetrics.fullText,
        sender_id: senderId || 'B2RCS',
        recipients,
        send_mode: sendMode,
        scheduled_at: sendMode === 'scheduled' ? `${scheduledDate}T${scheduledTime}` : null,
        recurring: sendMode === 'recurring' ? {
          pattern: recurringPattern,
          days: recurringDays,
          time: scheduledTime
        } : null
      };
      
      const response = await axios.post(`${API_URL}/sms-mailing/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Campagne ${sendMode === 'immediate' ? 'envoyée' : 'programmée'} avec succès !`);
      
      // Reset form
      setCampaignName('');
      setMessage('');
      setManualNumbers('');
      setUploadedFile(null);
      setFileContacts([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="sms-mailing-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <MessageSquare className="h-7 w-7 text-indigo-500" />
              SMS Mailing
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Envoyez des campagnes SMS en masse via B2SMS
            </p>
          </div>
          <Badge className="bg-indigo-100 text-indigo-700">
            B2SMS API
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Campaign Type */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                  Type de campagne
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={campaignType} onValueChange={setCampaignType} className="grid grid-cols-2 gap-4">
                  <div className={`flex items-start space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    campaignType === 'marketing'
                      ? 'border-sky-500 bg-sky-50'
                      : isDarkMode ? 'border-[hsl(220,14%,25%)] hover:border-[hsl(220,14%,35%)]' : 'border-slate-200 hover:border-slate-300'
                  }`} onClick={() => setCampaignType('marketing')}>
                    <RadioGroupItem value="marketing" id="marketing" className="mt-1" />
                    <div>
                      <Label htmlFor="marketing" className={`font-semibold cursor-pointer ${isDarkMode && campaignType !== 'marketing' ? 'text-slate-200' : ''}`}>
                        Marketing
                      </Label>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Promotions, offres commerciales
                      </p>
                      <Badge variant="outline" className="mt-2 text-xs border-sky-400 text-sky-600">
                        + stopsms.io (auto)
                      </Badge>
                    </div>
                  </div>
                  
                  <div className={`flex items-start space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    campaignType === 'transactional'
                      ? 'border-indigo-500 bg-indigo-50'
                      : isDarkMode ? 'border-[hsl(220,14%,25%)] hover:border-[hsl(220,14%,35%)]' : 'border-slate-200 hover:border-slate-300'
                  }`} onClick={() => setCampaignType('transactional')}>
                    <RadioGroupItem value="transactional" id="transactional" className="mt-1" />
                    <div>
                      <Label htmlFor="transactional" className={`font-semibold cursor-pointer ${isDarkMode && campaignType !== 'transactional' ? 'text-slate-200' : ''}`}>
                        Transactionnel
                      </Label>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Notifications, confirmations
                      </p>
                      <Badge variant="outline" className="mt-2 text-xs border-indigo-400 text-indigo-600">
                        Sans STOP
                      </Badge>
                    </div>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Campaign Name & Sender */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                  Informations campagne
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDarkMode ? 'text-slate-200' : ''}>Nom de la campagne *</Label>
                    <Input
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      placeholder="Ex: Promo Janvier 2026"
                      className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDarkMode ? 'text-slate-200' : ''}>Expéditeur (Sender ID)</Label>
                    <Input
                      value={senderId}
                      onChange={(e) => setSenderId(e.target.value.slice(0, 11))}
                      placeholder="Ex: MAMARQUE"
                      maxLength={11}
                      className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                    />
                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Max 11 caractères alphanumériques
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Message */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg flex items-center justify-between ${isDarkMode ? 'text-white' : ''}`}>
                  <span>Message</span>
                  <div className="flex items-center gap-2">
                    <Badge variant={smsMetrics.encoding === 'GSM-7' ? 'secondary' : 'destructive'}>
                      {smsMetrics.encoding}
                    </Badge>
                    <Badge variant={smsMetrics.smsCount <= 4 ? 'secondary' : 'destructive'}>
                      {smsMetrics.smsCount} SMS
                    </Badge>
                  </div>
                </CardTitle>
                <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                  {smsMetrics.chars} / {smsMetrics.maxChars} caractères ({smsMetrics.smsCount} SMS)
                  {campaignType === 'marketing' && (
                    <span className="text-indigo-600 ml-2">(+ stopsms.io auto)</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Tapez votre message ici...\n\nUtilisez {nom}, {prenom} pour personnaliser`}
                  rows={6}
                  className={`font-mono ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                />
                
                {/* Character count bar */}
                <div className="space-y-2">
                  <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-[hsl(220,14%,25%)]' : 'bg-slate-100'}`}>
                    <div 
                      className={`h-full transition-all ${
                        smsMetrics.chars > smsMetrics.maxChars 
                          ? 'bg-red-500' 
                          : smsMetrics.smsCount > 1 
                            ? 'bg-indigo-500' 
                            : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min((smsMetrics.chars / smsMetrics.maxChars) * 100, 100)}%` }}
                    />
                  </div>
                  
                  {smsMetrics.smsCount > 4 && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'}`}>
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <p className="text-sm text-red-600">
                        Message trop long ! Maximum recommandé : 4 SMS (459 caractères GSM-7)
                      </p>
                    </div>
                  )}
                </div>

                {/* Variables help */}
                {uploadedFile && fileColumns.length > 0 && (
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'}`}>
                    <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>
                      Variables disponibles :
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {fileColumns.map((col) => (
                        <Badge 
                          key={col}
                          variant="secondary"
                          className="cursor-pointer hover:bg-indigo-200"
                          onClick={() => setMessage(m => m + `{${col}}`)}
                        >
                          {`{${col}}`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recipients */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                  <Users className="h-5 w-5" />
                  Destinataires
                  {getRecipientsCount() > 0 && (
                    <Badge className="ml-auto">{getRecipientsCount()} contacts</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={recipientSource} onValueChange={setRecipientSource}>
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="file" className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      Fichier (CSV/Excel)
                    </TabsTrigger>
                    <TabsTrigger value="manual" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Saisie manuelle
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="file" className="space-y-4">
                    {!uploadedFile ? (
                      <div className={`border-2 border-dashed rounded-xl p-8 text-center ${isDarkMode ? 'border-[hsl(220,14%,25%)]' : 'border-slate-200'}`}>
                        <Upload className={`h-10 w-10 mx-auto mb-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                        <p className={`mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          Glissez votre fichier ou cliquez pour sélectionner
                        </p>
                        <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          Formats supportés : CSV, XLS, XLSX
                        </p>
                        <input
                          type="file"
                          accept=".csv,.xls,.xlsx"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="file-upload"
                        />
                        <Button asChild disabled={uploading}>
                          <label htmlFor="file-upload" className="cursor-pointer">
                            {uploading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Chargement...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Sélectionner un fichier
                              </>
                            )}
                          </label>
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className={`flex items-center justify-between p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <div className="flex items-center gap-3">
                            <FileSpreadsheet className="h-8 w-8 text-emerald-500" />
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                {uploadedFile.name}
                              </p>
                              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {fileContacts.length} contacts chargés
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setUploadedFile(null);
                              setFileContacts([]);
                              setFileColumns([]);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        {fileColumns.length > 0 && (
                          <div className="space-y-2">
                            <Label className={isDarkMode ? 'text-slate-200' : ''}>
                              Colonne téléphone *
                            </Label>
                            <Select value={phoneColumn} onValueChange={setPhoneColumn}>
                              <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                                <SelectValue placeholder="Sélectionnez la colonne" />
                              </SelectTrigger>
                              <SelectContent>
                                {fileColumns.map((col) => (
                                  <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="manual">
                    <Textarea
                      value={manualNumbers}
                      onChange={(e) => setManualNumbers(e.target.value)}
                      placeholder="Entrez les numéros (un par ligne ou séparés par des virgules)&#10;&#10;Ex:&#10;0612345678&#10;0698765432"
                      rows={6}
                      className={`font-mono ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Scheduling */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                  <Clock className="h-5 w-5" />
                  Programmation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={sendMode} onValueChange={setSendMode} className="space-y-3">
                  <div className={`flex items-center space-x-3 p-3 rounded-lg ${sendMode === 'immediate' ? 'bg-emerald-50 border border-emerald-200' : ''}`}>
                    <RadioGroupItem value="immediate" id="immediate" />
                    <Label htmlFor="immediate" className="flex items-center gap-2 cursor-pointer">
                      <PlayCircle className="h-4 w-4 text-emerald-600" />
                      <span className={isDarkMode && sendMode !== 'immediate' ? 'text-slate-200' : ''}>Envoi immédiat</span>
                    </Label>
                  </div>
                  
                  <div className={`flex items-center space-x-3 p-3 rounded-lg ${sendMode === 'scheduled' ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    <RadioGroupItem value="scheduled" id="scheduled" />
                    <Label htmlFor="scheduled" className="flex items-center gap-2 cursor-pointer">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      <span className={isDarkMode && sendMode !== 'scheduled' ? 'text-slate-200' : ''}>Envoi différé</span>
                    </Label>
                  </div>
                  
                  <div className={`flex items-center space-x-3 p-3 rounded-lg ${sendMode === 'recurring' ? 'bg-purple-50 border border-purple-200' : ''}`}>
                    <RadioGroupItem value="recurring" id="recurring" />
                    <Label htmlFor="recurring" className="flex items-center gap-2 cursor-pointer">
                      <RefreshCw className="h-4 w-4 text-purple-600" />
                      <span className={isDarkMode && sendMode !== 'recurring' ? 'text-slate-200' : ''}>Envoi récurrent</span>
                    </Label>
                  </div>
                </RadioGroup>
                
                {(sendMode === 'scheduled' || sendMode === 'recurring') && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    {sendMode === 'scheduled' && (
                      <div className="space-y-2">
                        <Label className={isDarkMode ? 'text-slate-200' : ''}>Date</Label>
                        <Input
                          type="date"
                          value={scheduledDate}
                          onChange={(e) => setScheduledDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                        />
                      </div>
                    )}
                    
                    {sendMode === 'recurring' && (
                      <div className="space-y-2">
                        <Label className={isDarkMode ? 'text-slate-200' : ''}>Fréquence</Label>
                        <Select value={recurringPattern} onValueChange={setRecurringPattern}>
                          <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Quotidien</SelectItem>
                            <SelectItem value="weekly">Hebdomadaire</SelectItem>
                            <SelectItem value="monthly">Mensuel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label className={isDarkMode ? 'text-slate-200' : ''}>Heure</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                      />
                    </div>
                  </div>
                )}
                
                {/* Warning for La Réunion */}
                <div className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'}`}>
                  <Info className="h-4 w-4 text-indigo-600 mt-0.5" />
                  <p className={`text-sm ${isDarkMode ? 'text-indigo-200' : 'text-indigo-700'}`}>
                    <strong>La Réunion :</strong> Pour les campagnes de plus de 30 000 SMS, privilégiez l'envoi entre 8h et 12h. 
                    Envoi interdit les dimanches et jours fériés.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Preview & Actions */}
          <div className="space-y-6">
            {/* Test */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                  Test d'envoi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className={isDarkMode ? 'text-slate-200' : ''}>Numéro de test</Label>
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="0612345678"
                    className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                  />
                </div>
                <Button 
                  onClick={handleSendTest}
                  disabled={testSending || !message}
                  variant="outline"
                  className="w-full"
                >
                  {testSending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Envoyer un test
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                  <Smartphone className="h-5 w-5" />
                  Aperçu
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center">
                  <div className="w-64 bg-slate-900 rounded-3xl p-4 shadow-2xl">
                    <div className="bg-white rounded-2xl p-4 min-h-[200px]">
                      <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                          <MessageSquare className="h-4 w-4 text-indigo-600" />
                        </div>
                        <span className="font-medium text-sm">{senderId || 'SMS'}</span>
                      </div>
                      <div className="space-y-2">
                        <div className="bg-slate-100 rounded-xl p-3 text-sm">
                          {smsMetrics.fullText || (
                            <span className="text-slate-400 italic">Votre message apparaîtra ici</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 text-right">
                          {smsMetrics.smsCount} SMS • {smsMetrics.encoding}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary & Send */}
            <Card className={`border-2 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-indigo-500/30' : 'border-indigo-200 bg-indigo-50'}`}>
              <CardHeader>
                <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                  Résumé
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className={`flex justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <span>Type</span>
                  <span className="font-medium">{campaignType === 'marketing' ? 'Marketing' : 'Transactionnel'}</span>
                </div>
                <div className={`flex justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <span>Destinataires</span>
                  <span className="font-medium">{getRecipientsCount()}</span>
                </div>
                <div className={`flex justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <span>SMS par message</span>
                  <span className="font-medium">{smsMetrics.smsCount}</span>
                </div>
                <div className={`flex justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <span>Total SMS</span>
                  <span className="font-bold text-lg text-indigo-600">{getRecipientsCount() * smsMetrics.smsCount}</span>
                </div>
                <div className={`flex justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <span>Envoi</span>
                  <span className="font-medium">
                    {sendMode === 'immediate' ? 'Immédiat' : sendMode === 'scheduled' ? 'Différé' : 'Récurrent'}
                  </span>
                </div>
                
                <div className="pt-4">
                  <Button 
                    onClick={handleSendCampaign}
                    disabled={sending || !campaignName || !message || getRecipientsCount() === 0}
                    className="w-full bg-indigo-500 hover:bg-indigo-600 h-12 text-lg font-semibold"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5 mr-2" />
                        {sendMode === 'immediate' ? 'Envoyer maintenant' : 'Programmer l\'envoi'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SMSMailing;
